import { useSales } from '../hooks/useSales'
import { useExpenses } from '../hooks/useExpenses'
import { usePrints } from '../hooks/usePrints'
import { useFilamentSpools } from '../hooks/useFilamentSpools'
import { toCsv, downloadCsv } from '../lib/csv'

export default function Export() {
  const { data: sales } = useSales()
  const { data: expenses } = useExpenses()
  const { data: prints } = usePrints()
  const { data: spools } = useFilamentSpools()

  const today = new Date().toISOString().slice(0, 10)

  const handleExportSales = () => {
    const csv = toCsv(sales ?? [], [
      { key: 'sale_date', label: 'Date' },
      { key: 'gross_amount', label: 'Gross ($)' },
      { key: 'platform_fees', label: 'Platform Fees ($)' },
      { key: 'shipping_charged', label: 'Shipping Charged ($)' },
      { key: 'shipping_cost', label: 'Shipping Cost ($)' },
      { key: 'platform', label: 'Platform' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'purpose', label: 'Purpose' },
      { key: 'notes', label: 'Notes' },
    ])
    downloadCsv(`sales-${today}.csv`, csv)
  }

  const handleExportExpenses = () => {
    const csv = toCsv(expenses ?? [], [
      { key: 'expense_date', label: 'Date' },
      { key: 'amount', label: 'Amount ($)' },
      { key: 'category', label: 'Category' },
      { key: 'description', label: 'Description' },
      { key: 'notes', label: 'Notes' },
    ])
    downloadCsv(`expenses-${today}.csv`, csv)
  }

  const handleExportPrints = () => {
    const csv = toCsv(prints ?? [], [
      { key: 'print_date', label: 'Date' },
      { key: 'purpose', label: 'Purpose' },
      { key: 'outcome', label: 'Outcome' },
      { key: 'units_produced', label: 'Units' },
      { key: 'print_time_hours', label: 'Print Time (h)' },
      { key: 'filament_cost_total', label: 'Filament Cost ($)' },
      { key: 'electricity_cost', label: 'Electricity Cost ($)' },
      { key: 'machine_cost', label: 'Machine Cost ($)' },
      { key: 'labor_cost', label: 'Labor Cost ($)' },
      { key: 'total_cost', label: 'Total Cost ($)' },
      { key: 'description', label: 'Description' },
    ])
    downloadCsv(`prints-${today}.csv`, csv)
  }

  const handleExportFilament = () => {
    const csv = toCsv(spools ?? [], [
      { key: 'brand', label: 'Brand' },
      { key: 'material', label: 'Material' },
      { key: 'color', label: 'Color' },
      { key: 'purchase_price', label: 'Purchase Price ($)' },
      { key: 'purchase_date', label: 'Purchase Date' },
      { key: 'original_weight_grams', label: 'Original Weight (g)' },
      { key: 'current_weight_grams', label: 'Current Weight (g)' },
      { key: 'status', label: 'Status' },
      { key: 'notes', label: 'Notes' },
    ])
    downloadCsv(`filament-${today}.csv`, csv)
  }

  const exports: Array<{ label: string; sub: string; icon: string; handler: () => void; count: number }> = [
    { label: 'Sales', sub: 'date, gross, fees, shipping, platform, customer', icon: '💰', handler: handleExportSales, count: (sales ?? []).length },
    { label: 'Expenses', sub: 'date, amount, category, description', icon: '🧾', handler: handleExportExpenses, count: (expenses ?? []).length },
    { label: 'Prints', sub: 'date, purpose, outcome, costs, settings', icon: '🖨️', handler: handleExportPrints, count: (prints ?? []).length },
    { label: 'Filament spools', sub: 'brand, material, color, weight, price', icon: '🧵', handler: handleExportFilament, count: (spools ?? []).length },
  ]

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <h1 style={{ marginBottom: 'var(--space-2)' }}>Export CSV</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-6)' }}>
        Download your data as CSV files. All data, no filters.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {exports.map((item) => (
          <li key={item.label} style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3)',
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-md)',
            }}>
              <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  {item.label}{' '}
                  <span style={{ color: 'var(--color-text-faint)', fontWeight: 400 }}>({item.count} rows)</span>
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                  {item.sub}
                </div>
              </div>
              <button
                onClick={item.handler}
                disabled={item.count === 0}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  background: item.count > 0 ? 'var(--color-primary)' : 'var(--color-surface-2)',
                  color: item.count > 0 ? 'white' : 'var(--color-text-faint)',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-xs)',
                  whiteSpace: 'nowrap',
                }}
              >
                Download
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
